import type { MinimalStepRuleModel } from '@/shared/generated'

import { pipe } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { useSettingsSchemasQuery } from '@/entities/core'

import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from '@/shared/components/ui/card'
import {
	DescriptionDetail,
	DescriptionGroup,
	DescriptionList,
	DescriptionTerm,
} from '@/shared/components/ui/description-list'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
	getResolvedSchema,
	translateSchemaKeys,
} from '@/shared/tools/json-schema-utils'

export const MinimalPriceStepView = ({
	settings,
}: {
	settings: MinimalStepRuleModel[]
}) => {
	const { t } = useTranslation()
	const settingsSchema = useSettingsSchemasQuery({
		select: pipe([
			getResolvedSchema('part:minimal_steps'),
			(schema) => translateSchemaKeys(schema, t),
		]),
	})

	if (settingsSchema.isLoading) {
		return (
			<Card className='space-y-4 p-4 shadow-none'>
				<MinimalPriceStepViewSkeleton />
			</Card>
		)
	}

	const schema = settingsSchema.data

	return (
		<Card className='shadow-none'>
			<CardHeader>
				<CardTitle>{schema.title}</CardTitle>
			</CardHeader>
			<CardContent className='flex flex-col gap-4 divide-y'>
				{settings.map((setting, index) => (
					<DescriptionList
						key={setting.min_price}
						className='flex flex-wrap gap-4'
					>
						<DescriptionGroup className='flex w-48 flex-col'>
							<DescriptionTerm>
								{t('general_range', {
									defaultValue: 'Range',
								})}
							</DescriptionTerm>
							<DescriptionDetail>
								{setting.min_price} -{' '}
								{settings[index + 1] ? settings[index + 1].min_price - 1 : '∞'}
							</DescriptionDetail>
						</DescriptionGroup>
						<DescriptionGroup className='flex w-24 flex-col'>
							<DescriptionTerm>
								{t('app.type', {
									defaultValue: 'Type',
								})}
							</DescriptionTerm>
							<DescriptionDetail>
								{setting.min_step_absolute > 0
									? t('absolute', {
											defaultValue: 'Absolute',
										})
									: t('relative', {
											defaultValue: 'Relative',
										})}
							</DescriptionDetail>
						</DescriptionGroup>
						<DescriptionGroup>
							<DescriptionTerm>
								{t('value', {
									defaultValue: 'Value',
								})}
							</DescriptionTerm>
							<DescriptionDetail>
								{setting.min_step_relative > 0
									? `${setting.min_step_relative}%`
									: `${setting.min_step_absolute} #`}
							</DescriptionDetail>
						</DescriptionGroup>
					</DescriptionList>
				))}
			</CardContent>
		</Card>
	)
}
const MinimalPriceStepViewSkeleton = () => (
	<>
		<Skeleton className='h-6 w-64' />
		<div className='space-y-2'>
			<Skeleton className='h-6 w-1/2' />
			<Skeleton className='h-6 w-1/2' />
		</div>
	</>
)
